import React, { useState } from 'react';
import { Check, Copy, Download, Share2, X } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';
import { ApiRelease } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';

/** Link that unfurls with the release's branded card (crawler page redirects people to /release/<slug>). */
export const releaseShareUrl = (slug: string) => {
  const u = mediaUrl(`/api/share/release/${encodeURIComponent(slug)}`);
  return u.startsWith('http') ? u : `${window.location.origin}${u}`;
};
export const releaseCardUrl = (slug: string, format: 'og' | 'story' | 'square', download = false) =>
  mediaUrl(`/api/share/release/${encodeURIComponent(slug)}/card.jpg?format=${format}${download ? '&download=true' : ''}`);

export default function SharePanel({ release, onClose }: { release: ApiRelease; onClose: () => void }) {
  const slug = release.slug || '';
  const link = releaseShareUrl(slug);
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.getElementById('ek-share-link') as HTMLInputElement | null;
      el?.select();
      document.execCommand?.('copy');
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const nativeShare = () =>
    navigator.share?.({ title: `${release.title} — The Ekuba Project`, text: `Listen to ${release.title} by The Ekuba Project`, url: link }).catch(() => {});

  return (
    <div className="ek-share" role="region" aria-label={`Share ${release.title}`} data-testid="release-share-panel">
      <div className="ek-share-top">
        <p className="ek-modal-kicker">SHARE THIS RELEASE</p>
        <button type="button" className="ek-share-x" onClick={onClose} aria-label="Close share options" data-testid="release-share-close-button">
          <X size={14} />
        </button>
      </div>
      <div className="ek-share-link">
        <input id="ek-share-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Share link" data-testid="release-share-link-input" />
        <button type="button" onClick={copy} className={copied ? 'is-done' : ''} data-testid="release-share-copy-button">
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <div className="ek-share-cards">
        <a className="ek-share-card is-story" href={releaseCardUrl(slug, 'story', true)} download data-testid="release-share-story-download">
          <img src={releaseCardUrl(slug, 'story')} alt="Instagram story image" loading="lazy" />
          <span>
            <span className="flex">{FaInstagram({ size: 12 })}</span> Story <Download size={12} />
          </span>
        </a>
        <a className="ek-share-card is-square" href={releaseCardUrl(slug, 'square', true)} download data-testid="release-share-square-download">
          <img src={releaseCardUrl(slug, 'square')} alt="Square post image" loading="lazy" />
          <span>
            <span className="flex">{FaInstagram({ size: 12 })}</span> Post <Download size={12} />
          </span>
        </a>
        <div className="ek-share-tip">
          <p>
            <b>Instagram:</b> post the story image, then add the link above with a <b>Link sticker</b>. Messages, WhatsApp, X and Facebook show the branded card automatically.
          </p>
          {canShare && (
            <button type="button" className="ek-share-native" onClick={nativeShare} data-testid="release-share-native-button">
              <Share2 size={14} /> Share…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
